/**
 * `{{token}}` resolver — client-side mirror of
 * `apps/backend/src/libs/correspondence/resolve-variables.ts`. Kept in sync
 * by hand. The backend is the source of truth for the email-builder preview;
 * this file only powers the message-composer card (where the AI emits tokens
 * but the card renders client-side, so we resolve here instead of round-
 * tripping through the server).
 */

import { computeAnrede, computeGrussformel } from './salutation';
import type {
  AnredeContext,
  GrussformelContext,
  SalutationCode,
} from './salutation';
import {
  EMAIL_VARIABLE_BY_TOKEN,
  type VariableDefinition,
} from './variable-catalog';

export interface VariableContext {
  recipient: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    salutation: SalutationCode;
    gender: string | null;
    businessPartnerName: string | null;
  } | null;
  sender: {
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  today?: Date;
}

export type RenderMode = 'html' | 'text';
export type OnMissing = 'keep' | 'drop' | 'throw';

export interface ResolveOptions {
  mode: RenderMode;
  onMissing?: OnMissing;
}

const TOKEN_PATTERN = /\{\{([a-z][a-z0-9._]*)\}\}/g;
/** Matches the TipTap variable wrapper `<span data-variable="...">…</span>`;
 * stripped in HTML mode so resolved emails don't carry empty marker spans. */
const VARIABLE_WRAPPER_SPAN_PATTERN =
  /<span\b[^>]*\bdata-variable\b[^>]*>([\s\S]*?)<\/span>/g;

export function resolveVariables(
  text: string,
  context: VariableContext,
  options: ResolveOptions,
): string {
  if (typeof text !== 'string' || text.length === 0) return text;

  let working = text;
  if (options.mode === 'html') {
    working = working.replace(VARIABLE_WRAPPER_SPAN_PATTERN, '$1');
  }
  if (working.indexOf('{{') < 0) return working;

  const onMissing = options.onMissing ?? 'keep';

  return working.replace(TOKEN_PATTERN, (match, token: string) => {
    const def = EMAIL_VARIABLE_BY_TOKEN.get(token);
    if (!def) return handleMissing(match, token, options.mode, onMissing);
    const value = lookup(token, def, context);
    if (value == null || value.length === 0) {
      return handleMissing(match, token, options.mode, onMissing);
    }
    return formatValue(value, def, options.mode);
  });
}

function lookup(
  token: string,
  def: VariableDefinition,
  ctx: VariableContext,
): string | null {
  switch (token) {
    case 'anrede':
      return computeAnredeFromContext(ctx);
    case 'grussformel':
      return computeGrussformelFromContext(ctx);
    case 'kontakt.vorname':
      return ctx.recipient?.firstName ?? null;
    case 'kontakt.nachname':
      return ctx.recipient?.lastName ?? null;
    case 'kontakt.email':
      return ctx.recipient?.email ?? null;
    case 'firma':
      return ctx.recipient?.businessPartnerName ?? null;
    case 'absender.vorname':
      return ctx.sender?.firstName ?? null;
    case 'absender.nachname':
      return ctx.sender?.lastName ?? null;
    case 'absender.email':
      return ctx.sender?.email ?? null;
    case 'datum':
      return formatDate(ctx.today ?? new Date());
    default:
      void def;
      return null;
  }
}

function computeAnredeFromContext(ctx: VariableContext): string | null {
  const anredeCtx: AnredeContext | null = ctx.recipient
    ? {
        firstName: ctx.recipient.firstName,
        lastName: ctx.recipient.lastName,
        salutation: ctx.recipient.salutation,
        gender: ctx.recipient.gender,
      }
    : null;
  if (!anredeCtx) return null;
  return computeAnrede(anredeCtx);
}

function computeGrussformelFromContext(ctx: VariableContext): string | null {
  const grussCtx: GrussformelContext = {
    duSie: ctx.recipient?.salutation ?? null,
    sender: ctx.sender
      ? { firstName: ctx.sender.firstName, lastName: ctx.sender.lastName }
      : null,
  };
  return computeGrussformel(grussCtx);
}

function formatValue(
  value: string,
  def: VariableDefinition,
  mode: RenderMode,
): string {
  if (mode === 'html') {
    const escaped = escapeHtml(value);
    return def.multiline ? escaped.replace(/\n/g, '<br/>') : escaped;
  }
  return def.multiline ? value.replace(/\n/g, ', ') : value;
}

function handleMissing(
  match: string,
  token: string,
  mode: RenderMode,
  onMissing: OnMissing,
): string {
  switch (onMissing) {
    case 'drop':
      return '';
    case 'throw':
      throw new UnresolvedVariableError(token);
    case 'keep':
    default:
      if (mode === 'html') {
        return `<span class="eb-var-unfilled" data-variable="${escapeHtml(
          token,
        )}">${escapeHtml(match)}</span>`;
      }
      return match;
  }
}

export class UnresolvedVariableError extends Error {
  constructor(public readonly token: string) {
    super(`Variable {{${token}}} could not be resolved.`);
    this.name = 'UnresolvedVariableError';
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(d: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}
